import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  useTheme,
} from "@mui/material";
import { cloudinaryService, numprice } from "@/helpers";
import { RecommendedItemProps } from "./RecommendedItem.types";

const RecommendedItem: React.FC<RecommendedItemProps> = ({
  item,
  onSelect,
  disabled = false,
}) => {
  const theme = useTheme();
  const displayPrice = numprice(item.pricePoint || 0);
  const imageUrl = item.images[0]?.url;

  // State for optimized image
  const [optimizedImage, setOptimizedImage] = React.useState<{
    url: string;
    isCloudinary: boolean;
    fallbackUsed: boolean;
  } | null>(null);

  const [imageLoading, setImageLoading] = React.useState<boolean>(false);

  // Effect to optimize item image
  React.useEffect(() => {
    if (!imageUrl) {
      setOptimizedImage(null);
      setImageLoading(false);
      return;
    }

    let isCancelled = false;
    setImageLoading(true);

    cloudinaryService
      .smartTransformImage(imageUrl, {
        width: 92,
        height: 92,
        crop: "fill",
        gravity: "auto",
        quality: "auto",
        format: "auto",
      })
      .then((result) => {
        if (isCancelled) return;

        setOptimizedImage({
          url: result.url,
          isCloudinary: result.isCloudinary,
          fallbackUsed: result.fallbackUsed,
        });

        if (result.fallbackUsed) {
          console.info(
            "RecommendedItem: Using fallback URL for item:",
            item.name
          );
        }
      })
      .catch((error) => {
        if (isCancelled) return;

        console.error("RecommendedItem: Image optimization failed:", error);
        setOptimizedImage({
          url: imageUrl,
          isCloudinary: false,
          fallbackUsed: true,
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setImageLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [imageUrl, item.name]);

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.action.hover,
        borderRadius: 4,
        width: "100%",
        maxWidth: 200,
        minWidth: 100,
        flex: "1 1 0",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:hover": !disabled
          ? {
              backgroundColor: theme.palette.action.selected,
              transform: "translateY(-2px)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.short,
              }),
            }
          : {},
      }}
    >
      <CardActionArea
        onClick={onSelect}
        disabled={disabled}
        sx={{
          padding: 0.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Product Image */}
        <CardMedia
          component="img"
          image={optimizedImage?.url || imageUrl}
          alt={item.name}
          sx={{
            width: "100%",
            aspectRatio: "1/1",
            borderRadius: 3,
            objectFit: "cover",
            marginBottom: 1,
            opacity: imageLoading ? 0.7 : 1,
            transition: theme.transitions.create("opacity", {
              duration: theme.transitions.duration.short,
            }),
          }}
        />

        {/* Product Info */}
        <CardContent
          sx={{
            padding: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {/* Product Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: 0.5,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "12px",
                textAlign: "center",
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                width: "100%",
              }}
            >
              {item.name}
            </Typography>
          </Box>

          {/* Price */}
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontWeight: 500,
              fontSize: 16,
            }}
          >
            {displayPrice}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default RecommendedItem;
